<template>
	<slot :items="items" :featuredItems="featured" :loading="loading" />
</template>

<script setup>
	const rotator = useRotator();
	const {generateThumbs} = useImages();
	const config = useAppConfig();

	let gtm;
	if (__GTM_TRACKING__) gtm = useGtm();

	const emit = defineEmits(['loadRotatorItems', 'fetch']);
	const model = defineModel();
	const props = defineProps({
		fetch: {
			type: Object,
			required: true,
		},
		transformItems: Function,
		transformFeaturedItems: Function,
		featured: Number,
		thumbPreset: String,
		featuredThumbPreset: String,
		ga4: {
			type: String,
			default: '',
		},
		gtmTracking: Object,
		dataKey: {
			type: String,
			default: null,
		},
		log: {
			type: Boolean,
			default: false,
		},
	});

	const loading = ref(true);
	const items = ref([]);
	let allItems = []; // Save original untransformed items
	const featured = ref([]);
	const res = await rotator.fetch(props.fetch, {dataKey: props.dataKey});

	// If multiple rotators are fetched, data is array of rotators. Otherwise it's array of items
	const multipleRotators = props.fetch?.code?.includes(',') ? true : false;
	if (res.data?.length) {
		items.value = multipleRotators ? res.data : res.data[0].items;
		allItems = items.value;
	}

	emit('fetch', {items: items.value});

	// Filter featured items if defined in props. Only works for single rotator
	if (!multipleRotators && items.value?.length && props.featured) {
		let d = res.data[0].items;
		featured.value = d.slice(0, props.featured);
		if (props.transformFeaturedItems) featured.value = props.transformFeaturedItems(featured.value);

		items.value = d.slice(props.featured);
	}

	// Transform items if transformItems prop is provided
	if (props.transformItems) {
		if (multipleRotators) {
			items.value = items.value.map(rotator => props.transformItems(rotator.items));
		}
		items.value = props.transformItems(items.value);
	}

	loading.value = false;

	// Ggenerate thumbs only for single rotator
	if (!multipleRotators) await generateImages();
	async function generateImages() {
		const thumbsPromises = [];

		if (items.value?.length && props.thumbPreset) {
			const thumbPromise = generateThumbs({
				data: items.value,
				preset: props.thumbPreset,
				dataKey: props.dataKey ? props.dataKey + '-thumbs' : null,
			});
			thumbsPromises.push(thumbPromise);
		}

		if (featured.value?.length && props.featuredThumbPreset) {
			const featuredThumbPromise = generateThumbs({
				data: featured.value,
				preset: props.featuredThumbPreset,
				dataKey: props.dataKey ? props.dataKey + '-thumbs-featured' : null,
			});
			thumbsPromises.push(featuredThumbPromise);
		}

		if (!thumbsPromises.length) return;

		try {
			await Promise.all(thumbsPromises);
		} catch (error) {
			useLog(['Error generating rotator thumbnails', error], 'error');
		}
	}

	onMounted(async () => {
		// gtm tracking
		if (props.gtmTracking && gtm) gtm.gtmTrack('viewPromotion', props.gtmTracking);

		// emit event with initial list
		emit('loadRotatorItems', {items: allItems, creativeSlot: props.ga4});

		// log
		if (props.log) console.log(`BaseCmsRotator ${props.fetch.code}`, items.value);
	});
	model.value = {
		allItems: allItems,
		items: items.value,
		featured: featured.value,
	};
</script>
